import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { tv } from '@/utils/styles';

import { TabOverflowProvider, useTabOverflowContext } from './context';

const TabsRoot = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & { buffer?: number }
>(({ className, buffer, ...props }, ref) => (
  <TabOverflowProvider buffer={buffer}>
    <TabsPrimitive.Root ref={ref} className={className} {...props} />
  </TabOverflowProvider>
));
TabsRoot.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { containerWidth } = useTabOverflowContext();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const tabListRef = React.useRef<HTMLDivElement>(null);
  const [shadowLeft, setShadowLeft] = useState(false);
  const [shadowRight, setShadowRight] = useState(false);

  const handleOnScroll = () => {
    const parent = scrollRef.current;
    const inner = innerRef.current;

    if (parent && inner) {
      const { scrollLeft } = parent;
      const maxScrollLeft = inner.scrollWidth - parent.clientWidth;
      setShadowLeft(scrollLeft > 0);
      setShadowRight(scrollLeft < maxScrollLeft - 1);
    }
  };

  useEffect(() => {
    handleOnScroll();
  }, [containerWidth]);

  useEffect(() => {
    const handleOnResize = () => {
      handleOnScroll();
    };

    const parent = scrollRef.current;
    if (parent) {
      parent.addEventListener('scroll', handleOnScroll);
      handleOnScroll(); // Initial check on mount
    }

    window.addEventListener('resize', handleOnResize);

    return () => {
      if (parent) {
        parent.removeEventListener('scroll', handleOnScroll);
        window.removeEventListener('resize', handleOnResize);
      }
    };
  }, []);

  const { wrapper, scroller, rightShadow, leftShadow, list } = tabsStyles();

  return (
    <div className={wrapper()}>
      <div className={scroller()} ref={scrollRef}>
        <span className={rightShadow()} style={{ display: shadowRight ? 'block' : 'none' }} />
        <span className={leftShadow()} style={{ display: shadowLeft ? 'block' : 'none' }} />
        <div ref={innerRef} style={{ width: containerWidth }}>
          <TabsPrimitive.List ref={tabListRef || ref} className={list({ className })} {...props} />
        </div>
      </div>
    </div>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { updateTabWidth } = useTabOverflowContext();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const tabWidth = triggerRef?.current?.getBoundingClientRect();
    updateTabWidth(props.value, tabWidth?.width || 0);

    return () => {
      // If tab is removed, set width to 0
      updateTabWidth(props.value, 0);
    };
  }, [updateTabWidth, props]);

  const { trigger } = tabsStyles();
  return <TabsPrimitive.Trigger ref={ref || triggerRef} className={trigger({ className })} {...props} />;
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => <TabsPrimitive.Content ref={ref} className={className} {...props} />);
TabsContent.displayName = TabsPrimitive.Content.displayName;

const TabIndicator = ({ children }: { children: React.ReactNode }) => {
  const { indicator } = tabsStyles();
  return <span className={indicator()}>{children}</span>;
};

export const tabsStyles = tv({
  slots: {
    wrapper: 'relative',
    scroller: 'no-scrollbar w-full overflow-x-auto overflow-y-hidden border-b border-grey-300 px-4 md:px-0',
    leftShadow:
      'absolute bottom-px left-0 top-0 z-2 w-4 shadow-[inset_10px_0px_8px_-8px_rgba(0,0,0,0.08)] transition-all',
    rightShadow:
      'absolute bottom-px right-0 top-0 z-2 w-4 shadow-[inset_-10px_0px_8px_-8px_rgba(0,0,0,0.08)] transition-all',
    list: 'grid auto-cols-min grid-flow-col gap-4',
    trigger:
      'group relative flex items-center gap-1 whitespace-nowrap py-3 text-baseSm text-grey-500 no-underline transition-all duration-100 after:absolute after:-bottom-px after:left-0 after:right-0 after:z-4 after:h-0 after:bg-lightBlue-500 after:transition-[height] hover:text-text-primary hover:after:h-[3px] data-[state=active]:text-text-primary data-[state=active]:after:h-[3px]',
    indicator:
      'ml-1 inline-flex min-w-4 justify-center rounded bg-grey-300 px-[3px] py-0.5 text-center text-xs font-medium text-blue-700 transition-[background-color] duration-100 group-data-[state=active]:bg-lightBlue-100',
  },
});

export { TabIndicator, TabsContent, TabsList, TabsRoot, TabsTrigger };
